'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { preload } from 'react-dom';
import { usePathname, useRouter } from 'next/navigation';
import projectServices from '@/utils/services/project';
import linkServices from "@/utils/services/link";
import { setProjectIdPath } from '@/utils/projectUtils';
import sectorService from '@/templates/Starglobal3D/utils/services/sector';
import ProjectSuspendedNotice from '@/components/ProjectSuspendedNotice';

declare global {
    interface Window {
        projectNamePath?: string;
    }
}

export interface SettingsScope {
    idPath: string;
    sublink: string;
    namePath?: string;
}

export interface IdPathContextType {
    projectValue: Record<string, unknown>;
    idPath: string;
    namePath?: string;
    sublink: string;
    isLoading: boolean;
    isSettingsLoading?: boolean;
    settingsScope?: SettingsScope | null;
    loadingLogoBootstrap?: { scope: SettingsScope; value: string | null } | null;
    error: string | null;
}

const IdPathContext = createContext<IdPathContextType>({
    idPath: "",
    namePath: "",
    sublink: "",
    projectValue: {},
    isLoading: true,
    isSettingsLoading: false,
    settingsScope: null,
    error: null,
});

interface IdPathProviderProps {
    children: React.ReactNode;
}

// Mapping từ URL path segment sang id_field thực tế của sector
const SECTOR_URL_MAPPING: Record<string, string> = {
    'agriculture': 'farm',
};

import { isLocale } from '@/utils/constant/locales';

export function IdPathProvider({ children }: IdPathProviderProps) {
    const [idPath, setIdPath] = useState<string>("");
    const [projectValue, setProjectValue] = useState<Record<string, unknown>>({});
    const [sublink, setSublink] = useState<string>("");
    const [namePath, setNamePath] = useState<string>("");
    const [settingsScope, setSettingsScope] = useState<SettingsScope | null>(null);
    const [loadingLogoBootstrap, setLoadingLogoBootstrap] = useState<IdPathContextType['loadingLogoBootstrap']>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSettingsLoading, setIsSettingsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [suspendedProject, setSuspendedProject] = useState<{ idPath: string; sublink: string } | null>(null);
    const fetchProjectSeqRef = React.useRef(0);
    const fetchIdPathSeqRef = React.useRef(0);
    const currentProjectPathRef = React.useRef<string | null>(null);
    const pathname = usePathname();
    const router = useRouter();

    useEffect(() => {
        // Loại bỏ prefix ngôn ngữ khỏi pathname để kiểm tra hệ thống định tuyến (ví dụ: /ja/map3d -> /map3d)
        let cleanPathname = pathname;
        const segments = pathname.split('/').filter(Boolean);
        if (segments.length > 0 && isLocale(segments[0])) {
            cleanPathname = '/' + segments.slice(1).join('/');
        }

        // Không chạy API nếu đang ở trang 404, admin, hoặc các trang system
        if (
            cleanPathname === '/404' ||
            cleanPathname.startsWith('/404') ||
            cleanPathname.startsWith('/admin') ||
            cleanPathname.startsWith('/activate') ||
            cleanPathname.startsWith('/verify') ||
            cleanPathname.startsWith('/reset-password') ||
            cleanPathname === '/' ||
            cleanPathname.startsWith('/api/') ||
            cleanPathname.startsWith('/sector') ||
            cleanPathname.startsWith('/model-viewer') ||
            cleanPathname.includes('/map3d') ||
            cleanPathname.includes('smart-holiday-360/le-quoc-khanh-2026') ||
            cleanPathname.includes('smart-digital-twin-360/vingroup')
        ) {
            currentProjectPathRef.current = null;
            setSuspendedProject(null);
            setIsLoading(false);
            setError(null);
            setIdPath("");
            setSublink("");
            setNamePath("");
            setProjectValue({});
            setSettingsScope(null);
            setLoadingLogoBootstrap(null);
            return;
        }

        const getProjectFromUrl = (): string | null => {
            const cleanPath = cleanPathname.replace(/^\/+|\/+$/g, '');
            // Strip known sub-paths from project URL (loan-view, admin, resolve, qr-landing, etc.)
            // e.g., "smart-exhibition-360/trung-tam/loan-view/13" → "smart-exhibition-360/trung-tam"
            const segments = cleanPath.split('/').filter(Boolean);
            const adminIndex = segments.findIndex(s => s === 'admin' || s === 'loan-view' || s === 'resolve' || s === 'qr-landing' || s === 'smart-facility');
            if (adminIndex !== -1) {
                return segments.slice(0, adminIndex).join('/');
            }
            return segments.join('/') || null;
        };

        let isCancelled = false;
        const currentSeq = ++fetchIdPathSeqRef.current;

        const fetchIdPath = async () => {
            const projectPath = getProjectFromUrl();

            if (!projectPath) {
                currentProjectPathRef.current = null;
                if (!isCancelled && currentSeq === fetchIdPathSeqRef.current) {
                    setError("Không xác định được project path từ URL");
                    setIdPath("");
                    setSublink("");
                    setNamePath("");
                    setProjectValue({});
                    setSettingsScope(null);
                    setLoadingLogoBootstrap(null);
                    setIsLoading(false);
                }
                return;
            }

            // Immediately and synchronously clear previous project state if navigating to a new project
            if (currentProjectPathRef.current !== projectPath) {
                currentProjectPathRef.current = projectPath;
                setNamePath(projectPath);
                setIdPath("");
                setSublink("");
                setProjectValue({});
                setSettingsScope(null);
                setLoadingLogoBootstrap(null);
            }
            setIsLoading(true);
            setError(null);

            try {
                // Kiểm tra xem đây có phải là id của 1 lĩnh vực (sector) không trước khi gọi API Project
                // Chỉ kiểm tra sector nếu URL là đường dẫn đơn (1 segment, ví dụ: /smart-tourism-360)
                // Dự án thông thường luôn có >= 2 segments (ví dụ: /smart-tourism-360/di-tich-ta-thiet)
                let isSectorPath = false;
                const pathSegments = projectPath.split('/').filter(Boolean);
                if (pathSegments.length <= 1) {
                    const urlSlug = projectPath.replace(/^smart-/, '').replace(/-360\/?$/, '').replace(/\/$/, '');
                    const resolvedIdField = SECTOR_URL_MAPPING[urlSlug] || urlSlug;
                    try {
                        const resSector = await sectorService.getAllNestedSectors();
                        if (isCancelled || currentSeq !== fetchIdPathSeqRef.current) return;
                        if (resSector?.data && Array.isArray(resSector.data)) {
                            isSectorPath = resSector.data.some((group: any) =>
                                group.fields?.some((field: any) => field.id_field === resolvedIdField || `${field.id_field}/` === resolvedIdField || `smart-${field.id_field}-360` === projectPath || `smart-${field.id_field}-360/` === projectPath)
                            );
                        }
                    } catch (e) {
                        console.error("Error checking sector fallback:", e);
                    }
                }

                if (isCancelled || currentSeq !== fetchIdPathSeqRef.current) {
                    return;
                }

                if (isSectorPath) {
                    // Đây là lĩnh vực hợp lệ, không redirect 404 mà bỏ qua logic Project
                    setIdPath("");
                    setSublink("");
                    setIsLoading(false);
                    return;
                }

                // Nếu không phải lĩnh vực, tiến hành kiểm tra dự án
                const response = await projectServices.getIdByFullNamePath({ name_path: projectPath, includeLoadingLogo: true });

                // Discard stale response if user navigated away or newer request started
                if (isCancelled || currentSeq !== fetchIdPathSeqRef.current) {
                    return;
                }

                // Dịch vụ chưa gia hạn: hiển thị thông báo tạm ngưng thay vì tải dự án
                const activeFlag = response?.data?.active ?? response?.active;
                if (activeFlag !== undefined && activeFlag !== null && Number(activeFlag) === 0) {
                    const suspendedIdPath = String(response?.data?.id ?? '');
                    const rawSublink = String(response?.data?.sublink ?? '').trim();
                    // link_id phải là id số: '/' hoặc rỗng -> trang chủ (1); tên link -> tra id, lỗi thì về 1
                    let suspendedSublink = '1';
                    if (/^\d+$/.test(rawSublink)) {
                        suspendedSublink = rawSublink;
                    } else if (suspendedIdPath && rawSublink && rawSublink !== '/') {
                        try {
                            const linkRes = await linkServices.getLinkByName({ id_path: suspendedIdPath, name: rawSublink.replace(/^\/|\/$/g, '') });
                            if (linkRes?.data?.id) suspendedSublink = String(linkRes.data.id);
                        } catch {
                            // giữ mặc định '1'
                        }
                    }
                    if (isCancelled || currentSeq !== fetchIdPathSeqRef.current) return;
                    setSuspendedProject({ idPath: suspendedIdPath, sublink: suspendedSublink });
                    setIdPath("");
                    setSublink("");
                    return;
                }
                setSuspendedProject(null);

                if (response.success === false || !response?.data) {
                    router.push('/404');
                    return;
                }
                setIdPath(response?.data?.id);
                const earlyLogo = response.data.loading_logo;
                const earlyLinkId = response.data.loading_logo_link_id;
                const hasEarlyLogo = (earlyLogo === null || typeof earlyLogo === 'string') &&
                    typeof earlyLinkId === 'string' && /^\d+$/.test(earlyLinkId);
                setSublink(hasEarlyLogo ? earlyLinkId : (response?.data?.sublink || "1"));
                setLoadingLogoBootstrap(hasEarlyLogo ? {
                    scope: { idPath: response.data.id, sublink: earlyLinkId, namePath: projectPath },
                    value: earlyLogo,
                } : null);
                if (hasEarlyLogo && typeof earlyLogo === 'string' && earlyLogo.trim()) {
                    preload(earlyLogo.trim(), { as: 'image', fetchPriority: 'high' });
                }

                // Store the real id_path for multi-tenant system
                if (response?.data?.id) {
                    setProjectIdPath(response.data.id);
                }

                // Store project info for verification redirects
                if (typeof window !== 'undefined' && projectPath) {
                    const pathSegments = projectPath.split('/').filter(Boolean);
                    if (pathSegments.length >= 2) {
                        const sector = pathSegments[0]; // e.g., 'products'
                        const projectName = pathSegments[1]; // e.g., 'heritage' 
                        const actualProjectName = pathSegments[2] || projectName; // actual project name

                        const projectInfo = {
                            sector: sector === 'products' ? actualProjectName : sector,
                            name: actualProjectName,
                            fullPath: projectPath,
                            id: response?.data?.id
                        };

                        localStorage.setItem('current_project_info', JSON.stringify(projectInfo));
                    }

                    window.id_path = response?.data?.id;
                    window.sublink = response?.data?.sublink;
                    window.projectNamePath = projectPath;
                }
            } catch (err) {
                if (!isCancelled && currentSeq === fetchIdPathSeqRef.current) {
                    console.error("API Error:", err);
                    setError(err instanceof Error ? err.message : 'Unknown error');
                    setIdPath("");
                    setSublink("");
                    router.push('/404');
                }
            } finally {
                if (!isCancelled && currentSeq === fetchIdPathSeqRef.current) {
                    setIsLoading(false);
                }
            }
        };

        fetchIdPath();

        return () => {
            isCancelled = true;
        };
    }, [pathname, router]);



    useEffect(() => {
        let isCancelled = false;
        const currentSeq = ++fetchProjectSeqRef.current;
        const currentIdPath = idPath;
        const currentSublink = sublink;
        const currentNamePath = namePath;

        if (!currentIdPath) {
            setProjectValue({});
            setSettingsScope(null);
            setLoadingLogoBootstrap(null);
            setIsSettingsLoading(false);
            return;
        }

        const fetchProject = async () => {
            setIsSettingsLoading(true);
            try {
                let finalSublink = currentSublink;
                const sublinkStr = String(currentSublink || "1");

                if (sublinkStr === "/" || sublinkStr === "1") {
                    finalSublink = "1";
                    if (currentSublink !== "1") {
                        if (isCancelled || currentSeq !== fetchProjectSeqRef.current) return;
                        setSublink("1");
                    }
                } else if (!isNaN(Number(sublinkStr))) {
                    finalSublink = sublinkStr;
                } else if (sublinkStr.includes('admin') || sublinkStr.includes('verify') || sublinkStr.includes('login') || sublinkStr.includes('register')) {
                    finalSublink = "1";
                    if (currentSublink !== "1") {
                        if (isCancelled || currentSeq !== fetchProjectSeqRef.current) return;
                        setSublink("1");
                    }
                } else {
                    const response = await linkServices.getLinkByName({ id_path: currentIdPath, name: sublinkStr.replace(/^\/|\/$/g, "") });
                    if (isCancelled || currentSeq !== fetchProjectSeqRef.current) return;

                    if (!response || response.success === false) {
                        finalSublink = !isNaN(Number(currentSublink)) ? currentSublink : "1";
                        if (currentSublink !== finalSublink) {
                            setSublink(finalSublink);
                            return;
                        }
                    } else if (response.data?.id) {
                        finalSublink = String(response.data.id);
                        if (currentSublink !== finalSublink) {
                            setSublink(finalSublink);
                        }
                    }
                }

                if (isCancelled || currentSeq !== fetchProjectSeqRef.current) return;

                const response = await projectServices.getAllVariableSettingAndValueFallback({ id_path: currentIdPath, sublink: finalSublink });
                if (isCancelled || currentSeq !== fetchProjectSeqRef.current) return;

                if (!response.error && !response?.data?.tour_xml) {
                    try {
                        const tourXmlResponse = await projectServices.getSettingByUuid({
                            id_path: currentIdPath,
                            uuid: "tour_xml",
                            sublink: finalSublink,
                        });
                        if (isCancelled || currentSeq !== fetchProjectSeqRef.current) return;

                        const tourXmlSetting = Array.isArray(tourXmlResponse?.data)
                            ? tourXmlResponse.data[0]
                            : tourXmlResponse?.data;
                        const tourXml = tourXmlSetting?.value ?? tourXmlSetting?.variable_value;

                        if (typeof tourXml === "string" && tourXml.trim()) {
                            response.data = { ...response.data, tour_xml: tourXml };
                        }
                    } catch (err) {
                        console.warn("Unable to load the tour XML setting:", err);
                    }
                }

                // Discard stale response if another project was requested or unmounted
                if (!isCancelled && currentSeq === fetchProjectSeqRef.current) {
                    if (!response.error) {
                        setProjectValue(response?.data || {});
                        setSettingsScope({
                            idPath: currentIdPath,
                            sublink: finalSublink,
                            namePath: currentNamePath,
                        });
                    }
                }
            } catch (err) {
                if (!isCancelled && currentSeq === fetchProjectSeqRef.current) {
                    console.error('Error fetching tour XML:', err);
                }
            } finally {
                if (!isCancelled && currentSeq === fetchProjectSeqRef.current) {
                    setIsSettingsLoading(false);
                }
            }
        };

        fetchProject();

        return () => {
            isCancelled = true;
        };
    }, [idPath, sublink, namePath]);



    return (
        <IdPathContext.Provider
            value={{
                idPath,
                namePath,
                sublink,
                projectValue,
                isLoading,
                isSettingsLoading,
                settingsScope,
                loadingLogoBootstrap,
                error,
            }}
        >
            {suspendedProject ? <ProjectSuspendedNotice idPath={suspendedProject.idPath} sublink={suspendedProject.sublink} /> : children}
        </IdPathContext.Provider>
    );
}

export const useIdPath = () => {
    const context = useContext(IdPathContext);
    if (!context) {
        throw new Error('useIdPath must be used within IdPathProvider');
    }
    return context;
};
